import {
  CabinCategoryCfmModel,
  IGetShipsResponse,
  MyAreaBookingPaymentProps,
} from '@msc-b2c/interfaces/types';
import {
  AppDispatch,
  FinalizePaymentMyMsc,
  fetchContentFragmentsFromCms,
  getActiveSession,
  isChain2,
  isProduction,
  myAreaCabinDetails,
  myAreaBookingPayment,
  myAreaFinalizeBookingPayment,
  PaymentContext,
  PriceToBookCruiseMyMsc,
  RootState,
  setIsErrorModalOpen,
  setShipCF,
  useGetBookingQuery,
  membershipsData,
} from '@msc-b2c/shared-utils';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  getCabinObjectOfTheLoggedUser,
  getMembershipData,
  isPaymentEnabled,
} from '../utils/myAreaUtils';
import { usePrivateAreaPageViewTracking } from '../utils/analyticsUtils';
import {
  FinalizePaymentMyMscResponse,
  FinalizePaymentMyMscRequest,
  IFetchShipsFromFragmentsURLParams,
  PriceToBookCruiseMyMscRequest,
  PriceToBookCruiseMyMscUrlParams,
} from '@msc-b2c/interfaces/interfaces';
import { useDispatch, useSelector } from 'react-redux';
import { Payment } from './Payment';
import { useCallback, useEffect, useRef } from 'react';
import { LoginMode } from '@msc-b2c/interfaces/enums';

export function PaymentWrapper({
  paymentMainTitle,
  paymentMethodsTitle,
  paymentMethodsDescription,
  depositSectionTitle,
  depositSectionDescription,
  fullAmountSectionTitle,
  fullAmountDescription,
  dateFormat,
  cardPaymentDetailsLabel,
  cardNumberPlaceholder,
  cardExpirationDatePlaceholder,
  cardCvvPlaceholder,
  cardCvvDisclaimer,
  termsAndConditionSection,
  paymentButtonCTA,
  refundableDepositBanner,
  summaryTitle,
  fullAmountSummaryText,
  depositSummaryText,
  depositBookingText,
  amountPaidLabel,
  datatrans,
  numberOfItem,
  paymentSummaryClosedAccordionIcon,
  paymentSummaryOpenAccordionIcon,
  confirmation,
  defaultPaymentType,
  firstPaymentType,
  errorModalTitle,
  errorModalDescription,
  errorModalCTALabel,
  mandatoryLabel,
  enableAvs,
  avsFormInputField,
}: Readonly<MyAreaBookingPaymentProps>) {
  const dispatch = useDispatch<AppDispatch>();
  const { cabinDetails, membershipData } = useSelector((state: RootState) => state.myCruise);
  usePrivateAreaPageViewTracking();

  // if the booking has remaining balance or if it's in status OPT, show all, otherwise return null
  const { bookingNumber = '', loginMode } = getActiveSession();
  // GET BOOKING API CALL
  const { data: booking } = useGetBookingQuery(bookingNumber || skipToken);
  // check if payment is enabled otherwise the component should not render
  const paymentEnabled: boolean = isPaymentEnabled(booking);
  const hasCalledApi = useRef(false);
  const membershipRequestInProgress = useRef(false);

  const fetchShipDataFromFragments = useCallback(() => {
    const shipCode = booking?.cabins?.[0]?.cruise?.ship?.code.slice(0, 2) ?? '';
    if (!shipCode) return;

    fetchContentFragmentsFromCms<IGetShipsResponse, IFetchShipsFromFragmentsURLParams>('getShips', {
      shipCode,
    })
      .then((result) => {
        const shipList = result?.data?.shipCfmList?.items;
        const selectedShip = shipList?.[0];

        if (selectedShip) {
          dispatch(setShipCF(selectedShip));
        } else {
          dispatch(setIsErrorModalOpen(true));
        }

        const cabinOfTheLoggedUser = getCabinObjectOfTheLoggedUser(booking);
        const categoryCode = cabinOfTheLoggedUser?.categoryCode;
        const cabinCategoryInterior = selectedShip?.interiorCategoryCf?.find(
          (item) => item.cabinCode === categoryCode
        );
        const cabinCategoryOceanView = selectedShip?.oceanViewCategoryCf?.find(
          (item) => item.cabinCode === categoryCode
        );
        const cabinCategoryBalcony = selectedShip?.balconyCategoryCf?.find(
          (item) => item.cabinCode === categoryCode
        );
        const cabinCategorySuite = selectedShip?.suiteCategoryCf?.find(
          (item) => item.cabinCode === categoryCode
        );
        const cabinCategoryYachtClub = selectedShip?.yachtClubCategoryCf?.find(
          (item) => item.cabinCode === categoryCode
        );

        const cabinCategory: CabinCategoryCfmModel | undefined =
          cabinCategoryInterior ??
          cabinCategoryOceanView ??
          cabinCategoryBalcony ??
          cabinCategorySuite ??
          cabinCategoryYachtClub ??
          undefined;

        if (cabinCategory) {
          dispatch(myAreaCabinDetails(cabinCategory));
        }
      })
      .catch((error) => {
        console.error('Error fetching ship content fragment:', error);
        dispatch(setIsErrorModalOpen(true));
      });
  }, [booking]);

  const onPriceToBookMyMsc = () => {
    const urlParams: PriceToBookCruiseMyMscUrlParams = {
      bookingNumber: bookingNumber,
    };
    const priceToBookCruiseMyMscRequestData: PriceToBookCruiseMyMscRequest = {
      returnPath:
        (isProduction() || isChain2() ? '' : globalThis.location.origin) +
        `${globalThis.location.pathname}-paymentredirection${globalThis.location.search}`,
    };

    const priceToBookCruiseMyMsc = new PriceToBookCruiseMyMsc(
      false,
      false,
      priceToBookCruiseMyMscRequestData,
      urlParams
    );

    priceToBookCruiseMyMsc
      .sendData()
      .then((res) => {
        dispatch(myAreaBookingPayment(res.paymentOptions));
      })
      .catch((error) => {
        console.log('Error', error);
      });
  };

  useEffect(() => {
    if (membershipRequestInProgress.current || loginMode !== LoginMode.FULL || membershipData) {
      return;
    }

    membershipRequestInProgress.current = true;
    getMembershipData()
      .then((res) => {
        dispatch(membershipsData(res?.memberships ? res : { memberships: [] }));
      })
      .finally(() => {
        membershipRequestInProgress.current = false;
      });
  }, [dispatch, loginMode, membershipData]);

  useEffect(() => {
    if (hasCalledApi.current) {
      return;
    }
    hasCalledApi.current = true;
    // call API 145 - Price to book Cruise
    onPriceToBookMyMsc?.();
    return () => {
      dispatch(myAreaFinalizeBookingPayment(null));
    };
  }, []);

  useEffect(() => {
    const cruiseId = booking?.cabins?.[0]?.cruise?.id;
    const shouldFetchShipData = bookingNumber && cruiseId && !cabinDetails;

    if (shouldFetchShipData) {
      fetchShipDataFromFragments();
    }
  }, [booking, bookingNumber, cabinDetails, fetchShipDataFromFragments]);

  if (!paymentEnabled) {
    return;
  }

  const onFinalizePaymentMyMsc = (
    data?: any,
    onSuccess?: (res: any) => void,
    onError?: (error: any) => void,
    onFinally?: () => void
  ) => {
    const { transactionId, type, billing } = data;

    const finalizePaymentMyMSCRequestData: FinalizePaymentMyMscRequest = {
      transactionId,
      type: type === 'FULL' ? 'FULL' : 'DEPOSIT',
      ...(billing && { billing }),
    };

    const urlParams = {
      bookingNumber: bookingNumber,
    };

    const finalizePayment = new FinalizePaymentMyMsc(
      false,
      false,
      finalizePaymentMyMSCRequestData,
      urlParams
    );
    finalizePayment
      .sendData()
      .then((res: any) => {
        dispatch(myAreaFinalizeBookingPayment(res));
        onSuccess?.(res);
      })
      .catch((error: any) => {
        const failedPaymentResponse: FinalizePaymentMyMscResponse = {
          transactionId,
          type,
          _warnings: [
            {
              code: error?.response?.data?.code || error?.code || 'PAYMENT_FAILURE',
              message:
                error?.response?.data?.message || error?.message || 'Payment finalization failed',
              traceId: error?.response?.data?.traceId || '',
              errorId: error?.response?.data?.errorId || '',
              dateTime: error?.response?.data?.dateTime || new Date().toISOString(),
            },
          ],
        };

        dispatch(myAreaFinalizeBookingPayment(failedPaymentResponse));
        dispatch(myAreaBookingPayment(null));
        onError?.(error);
      })
      .finally(() => {
        onFinally?.();
      });
  };

  return (
    <PaymentContext.Provider
      value={{
        onFinalizePayment: onFinalizePaymentMyMsc,
        onPriceToBook: onPriceToBookMyMsc,
      }}
    >
      <Payment
        mandatoryLabel={mandatoryLabel}
        paymentMainTitle={paymentMainTitle}
        paymentMethodsTitle={paymentMethodsTitle}
        paymentMethodsDescription={paymentMethodsDescription}
        depositSectionTitle={depositSectionTitle}
        depositSectionDescription={depositSectionDescription}
        fullAmountSectionTitle={fullAmountSectionTitle}
        fullAmountDescription={fullAmountDescription}
        dateFormat={dateFormat}
        cardPaymentDetailsLabel={cardPaymentDetailsLabel}
        cardNumberPlaceholder={cardNumberPlaceholder}
        cardExpirationDatePlaceholder={cardExpirationDatePlaceholder}
        cardCvvPlaceholder={cardCvvPlaceholder}
        cardCvvDisclaimer={cardCvvDisclaimer}
        termsAndConditionSection={termsAndConditionSection}
        paymentButtonCTA={paymentButtonCTA}
        refundableDepositBanner={refundableDepositBanner}
        summaryTitle={summaryTitle}
        fullAmountSummaryText={fullAmountSummaryText}
        depositSummaryText={depositSummaryText}
        depositBookingText={depositBookingText}
        amountPaidLabel={amountPaidLabel}
        datatrans={datatrans}
        confirmation={confirmation}
        numberOfItem={numberOfItem}
        paymentSummaryClosedAccordionIcon={paymentSummaryClosedAccordionIcon}
        paymentSummaryOpenAccordionIcon={paymentSummaryOpenAccordionIcon}
        firstPaymentType={firstPaymentType}
        defaultPaymentType={defaultPaymentType}
        errorModalTitle={errorModalTitle}
        errorModalDescription={errorModalDescription}
        errorModalCTALabel={errorModalCTALabel}
        enableAvs={enableAvs}
        avsFormInputField={avsFormInputField}
      />
    </PaymentContext.Provider>
  );
}

export default PaymentWrapper;
PaymentWrapper.aemResourceType = 'b2c-website-foundation/components/mymsc/payment';
